/**
 * EN-1569: what this browser does when anyone saves a pig run's user
 * notification list (ReceivePigRunNotificationsChanged, see
 * utils/PigRunNotificationsChangedRefresh.ts).
 *
 * Debounced per run (1 s). Then, best effort:
 * 1. re-reads the two viewer lists for the signed-in user;
 * 2. tells the run's progress cards to re-read their list
 *    (pigRunNotificationsRefresh);
 * 3. shows a short toast, unless this browser saved that run's list in the
 *    last 5 s, or the user can see the run neither before nor after.
 * Nothing here throws.
 */
import { hasAnyRole } from '../../utils/AuthGuard';
import { isSignedIn } from '../../utils/DownloadCommandsChangedRefresh';
import {
  PIG_RUN_NOTIFICATIONS_PUSH_DELAY_MS,
  PIG_RUN_NOTIFICATIONS_REFRESH_ACTION,
  isPigRunNotificationsChangedMsg,
  notificationsChangedMessage,
  wasPigRunNotificationsSavedHere,
} from '../../utils/PigRunNotificationsChangedRefresh';

export interface PigRunNotificationsTimers {
  setTimeout: (callback: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

const realTimers: PigRunNotificationsTimers = {
  setTimeout: (callback, ms) => setTimeout(callback, ms),
  clearTimeout: (handle) =>
    clearTimeout(handle as ReturnType<typeof setTimeout>),
};

let timers: PigRunNotificationsTimers = realTimers;

// Pending refresh per run id.
const pending = new Map<number, unknown>();

/** Replaces setTimeout/clearTimeout (unit specs); null restores them. */
export function setPigRunNotificationsTimers(
  value: PigRunNotificationsTimers | null
): void {
  for (const handle of pending.values()) timers.clearTimeout(handle);
  pending.clear();
  timers = value ?? realTimers;
}

/**
 * True when this user sees the run in the live view: the run is loaded and,
 * for a viewer, it is in their "runs I am subscribed to" list (the same rule
 * as Default.vue's activePigRunsFiltered).
 */
export function canSeePigRun(rootState: any, runId: number): boolean {
  if (!rootState?.pigRun?.pigRuns?.[runId]) return false;
  if (!hasAnyRole(rootState.user, 'viewer')) return true;
  const list = rootState.activePigRunArrayWithNotification;
  return Array.isArray(list) && list.some((id: unknown) => id == runId);
}

async function refreshAfterPush(context: any, runId: number): Promise<void> {
  try {
    const user = context.rootState?.user;
    if (!isSignedIn(user)) return;
    const visibleBefore = canSeePigRun(context.rootState, runId);

    // Both actions catch and log their own errors; allSettled is a second guard.
    await Promise.allSettled([
      Promise.resolve().then(() =>
        context.dispatch('getActivePigRunListForUserWithNotification', user.id)
      ),
      Promise.resolve().then(() =>
        context.dispatch(
          'getActivePigRunUidListForUserWithNotification',
          user.id
        )
      ),
    ]);
    if (!isSignedIn(context.rootState?.user)) return;

    context.dispatch(PIG_RUN_NOTIFICATIONS_REFRESH_ACTION, { runId });

    if (wasPigRunNotificationsSavedHere(runId)) return;
    if (!visibleBefore && !canSeePigRun(context.rootState, runId)) return;
    const name = context.rootState?.pigRun?.pigRuns?.[runId]?.pigRun?.name;
    context.dispatch('notify', notificationsChangedMessage(name));
  } catch (error) {
    console.error(
      'Dispatch action = [pigRunNotificationsChangedReceived]. Refresh failed.',
      error
    );
  }
}

export default {
  state: {},
  mutations: {},
  actions: {
    /** ReceivePigRunNotificationsChanged: debounced refresh of that run. */
    pigRunNotificationsChangedReceived(context: any, payload?: unknown) {
      if (!isPigRunNotificationsChangedMsg(payload)) return;
      if (!isSignedIn(context.rootState?.user)) return;
      const runId = payload.runId;
      const existing = pending.get(runId);
      if (existing != null) timers.clearTimeout(existing);
      pending.set(
        runId,
        timers.setTimeout(() => {
          pending.delete(runId);
          void refreshAfterPush(context, runId);
        }, PIG_RUN_NOTIFICATIONS_PUSH_DELAY_MS)
      );
    },
    pigRunNotificationsRefresh(_context: any, _payload?: unknown) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
  },
};
